import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import "./AbLineAlignmentBanner.less";

export type BannerVariant = "warning" | "success";

interface AbLineAlignmentBannerProps {
  variant: BannerVariant;
  onSnapToAbLine?: () => void;
  onClose: () => void;
  /** List of AB lines available for alignment selection. */
  abLines?: Array<{ name: string }>;
  /** Index of the currently selected AB line (default 0). */
  selectedAbLineIndex?: number;
  /** Called when the user picks a different AB line. */
  onSelectAbLine?: (index: number) => void;
}

const WarningIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M0 4V0H2V4H0Z" fill="#E99921" />
    <path d="M0 10V6H2V10H0Z" fill="#E99921" />
    <path d="M0 12V16H2V12H0Z" fill="#E99921" />
    <path d="M14 4V0H16V4H14Z" fill="#E99921" />
    <path d="M14 10V6H16V10H14Z" fill="#E99921" />
    <path d="M14 12V16H16V12H14Z" fill="#E99921" />
    <path d="M12 4L8 0L4 4H7V16H9V4H12Z" fill="#E99921" />
  </svg>
);

const SuccessIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM8 12.1681L14.59 5.57812L16 6.99813L8 14.9981L4 10.9981L5.41 9.58813L8 12.1681Z"
      fill="#19A04B"
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
      fill="#C2C7D0"
    />
  </svg>
);

const CheckmarkSmallIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 6l3.5 3.5L11 2"
      stroke="#19A04B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AbLineAlignmentBanner: React.FC<AbLineAlignmentBannerProps> = ({
  variant,
  onSnapToAbLine,
  onClose,
  abLines,
  selectedAbLineIndex = 0,
  onSelectAbLine,
}) => {
  const { t } = useTranslation();
  // Keep a stable ref to the latest onClose so the auto-dismiss timer
  // is not restarted when the parent re-renders (new inline function reference).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Auto-dismiss the success banner after 3 seconds
  useEffect(() => {
    if (variant !== "success") return;
    const timer = setTimeout(() => {
      onCloseRef.current();
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [variant]); // intentionally omit onClose – using ref above

  const isSuccess = variant === "success";
  const hasMultipleAbLines = !isSuccess && !!abLines && abLines.length > 1;

  return (
    <div
      className={`ab-alignment-banner ab-alignment-banner--${variant}`}
      role="alert"
      aria-live="polite"
    >
      {/* ── Main row: icon + message + (snap btn for single AB) + close ── */}
      <div className="ab-alignment-banner__main-row">
        {isSuccess ? <SuccessIcon /> : <WarningIcon />}
        <span className="ab-alignment-banner__message">
          {isSuccess
            ? formatTranslation(t("newTrial.trialMap.abLine.aligned"))
            : formatTranslation(t("newTrial.trialMap.abLine.notAligned"))}
        </span>
        {!isSuccess && !hasMultipleAbLines && onSnapToAbLine && (
          <button
            className="ab-alignment-banner__snap-btn"
            onClick={onSnapToAbLine}
            type="button"
          >
            {formatTranslation(t("newTrial.trialMap.abLine.snapToAbLine"))}
          </button>
        )}
        <button
          className="ab-alignment-banner__close-btn"
          onClick={onClose}
          type="button"
          aria-label={formatTranslation(t("newTrial.trialMap.abLine.dismiss"))}
        >
          <CloseIcon />
        </button>
      </div>

      {/* ── Selector row: AB line list + snap btn (warning + multiple AB only) ── */}
      {hasMultipleAbLines && (
        <div className="ab-alignment-banner__selector-row">
          <div className="ab-alignment-banner__ab-selector">
            {(abLines ?? []).map((ab, idx) => (
              <button
                key={idx}
                type="button"
                className={`ab-alignment-banner__ab-item${
                  idx === selectedAbLineIndex
                    ? " ab-alignment-banner__ab-item--selected"
                    : ""
                }`}
                onClick={() => onSelectAbLine?.(idx)}
                aria-pressed={idx === selectedAbLineIndex}
              >
                {idx === selectedAbLineIndex && <CheckmarkSmallIcon />}
                {ab.name}
              </button>
            ))}
          </div>
          {onSnapToAbLine && (
            <button
              className="ab-alignment-banner__snap-btn"
              onClick={onSnapToAbLine}
              type="button"
            >
              {formatTranslation(t("newTrial.trialMap.abLine.snapToAbLine"))}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AbLineAlignmentBanner;
